import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
    has as xHas,
} from 'lodash';
import { useA360 } from '../contexts/a360';

function AutodeskModelView(props, ref) {

    const { visible, a360_hub, a360_project, a360_file, setYaw, setPitch } = props;
    const { a360GetTokenInfo, a360GetProjectItem } = useA360();

    const [ isAutodeskViewerLocked, lockAutodeskViewer ] =  useState(true);

    const autodeskViewerRef = useRef();
    const autodeskViewerContainerRef = useRef(null);
    const modelViewData = useRef({
        paperToModel:new DOMMatrix(),
        modelToPaper:new DOMMatrix(),
        refPointTransformation:[1,0,0, 0,1,0, 0,0,1, 0,0,0],
        refPointTr:new THREE.Matrix4()
    });

    const selectedPlan = useSelector(state => state.project.selectedPlan);
    const selectedViewpointId = useSelector(state => state.project.selectedViewpointId);

    const leftYaw = useSelector(state => state.project.leftYaw);
    const leftPitch = useSelector(state => state.project.leftPitch);
    const leftHfov = useSelector(state => state.project.leftHfov);
    const splitView = useSelector(state => state.project.splitView);

    const updateAutocadViewerCameraTarget = (yaw, pitch, hfovDegrees) => {
        if (autodeskViewerRef.current) {
            //const viewerElement = document.getElementById('viewer');
            //if (viewerElement && viewerElement.style.visibility !== 'hidden')
            {
                const { width, height } = autodeskViewerRef.current?.canvas || {width:0,height:0};
                const fov = (width > 0) ? hfovDegrees * height / width : 45;

                let tpitch = pitch;
                if (tpitch <= -70)
                    tpitch = -70;
                else if (tpitch > 70)
                    tpitch = 70;

                const e = new THREE.Euler(0,-tpitch*Math.PI/180,yaw*Math.PI/180,'ZYX');
                const m = (new THREE.Matrix4()).makeRotationFromEuler(e);
                const ax = new THREE.Vector3();
                const ay = new THREE.Vector3();
                const az = new THREE.Vector3();
                m.extractBasis(ax, ay, az);

                const camera = autodeskViewerRef.current.getCamera().clone();
                const target = camera.position.clone();
                target.add(ax.multiplyScalar(1000));

                autodeskViewerRef.current.navigation.setView(
                    camera.position,
                    target
                );
                autodeskViewerRef.current.navigation.setVerticalFov(fov, false);
                autodeskViewerRef.current.navigation.setCameraUpVector(new THREE.Vector3(0, 0, 1));
            }
        }
    };


    const createModelPlanMatrices = (plan) => {
        if (!plan.relation_data) {
            return {
                paperToModel:new DOMMatrix(),
                modelToPaper:new DOMMatrix()
            }
        }

        const rel = plan.relation_data;

        const ldx = rel.plan.scaleEnd.x - rel.plan.scaleStart.x;
        const ldy = rel.plan.scaleEnd.y - rel.plan.scaleStart.y;

        const sf = rel.a360model.scaleLength / Math.sqrt(ldx * ldx + ldy * ldy);

        const ox = rel.plan.origin.x;
        const oy = rel.plan.origin.y;
        const ax = rel.a360model.origin.x;
        const ay = rel.a360model.origin.y;

        const mxTrans = (new THREE.Matrix4()).makeTranslation(-ox, -oy, 0);
        const mxScale = (new THREE.Matrix4()).makeScale(sf, sf, sf);
        const mxRot = (new THREE.Matrix4()).makeRotationZ(rel.plan.rotation);
        const mxTransBack = (new THREE.Matrix4()).makeTranslation(ax, ay, 0);

        const paperToModel = mxTransBack.multiply(mxRot).multiply(mxScale).multiply(mxTrans);
        const modelToPaper = (new THREE.Matrix4()).getInverse(paperToModel);

        return {
            paperToModel,
            modelToPaper
        };
    }


    const setAutocadViewerCameraToViewpoint = useCallback((plan, viewpoint) => {

        if (!autodeskViewerRef.current)
            return;
        if (!viewpoint)
            return;

        const rp = modelViewData.current.refPointTransformation;
        const refPointTr = (new THREE.Matrix4()).set(
            rp[0], rp[3], rp[6], rp[9],
            rp[1], rp[4], rp[7], rp[10],
            rp[2], rp[5], rp[8], rp[11],
            0, 0, 0, 1,
        );
        const refPointTrInv = (new THREE.Matrix4()).getInverse(refPointTr);

        const unitScale = autodeskViewerRef.current.model.getUnitScale() || 1.0;

        const vpTr = new DOMMatrix(plan.viewpoint_trafo);

        const vpPaper = (new DOMPoint(viewpoint.x, viewpoint.y)).matrixTransform(vpTr);

        const vpModel = (new THREE.Vector3(vpPaper.x, vpPaper.y, 0)).applyMatrix4(modelViewData.current.paperToModel);
        vpModel.z = plan.relation_data.a360model.origin.z + 1.8 / unitScale;
        vpModel.applyMatrix4(refPointTrInv);

        const camtrg = new THREE.Vector3(vpModel.x, vpModel.y + 100, vpModel.z);
        autodeskViewerRef.current.navigation.setView(
            vpModel.applyMatrix4(autodeskViewerRef.current.model.getModelToViewerTransform()),
            camtrg.applyMatrix4(autodeskViewerRef.current.model.getModelToViewerTransform())
        );
        autodeskViewerRef.current.navigation.setCameraUpVector(new THREE.Vector3(0, 0, 1));

        const d = (plan?.direction || 0) * 180.0 / Math.PI;

        let tyaw = 360 - (leftYaw + d - 90);
        while (tyaw >= 360) tyaw -= 360;

//        const panoViewer = leftPanoViewerRef.current.getViewer();

        updateAutocadViewerCameraTarget(tyaw, leftPitch, leftHfov);
    },[leftYaw, leftPitch, leftHfov]);


    const initializeAutodeskViewer = useCallback(() => {
        return new Promise((resolve, reject) => {

            if (autodeskViewerRef.current) {
                resolve();
                return;
            }

            if (!Autodesk || !THREE) {
                reject('Autodesk and THREE are required.');
                return;
            }

            a360GetTokenInfo()
                .then((tokenInfo) => {

                    const options = {
                        env: 'AutodeskProduction',
                        api: 'derivativeV2_EU',  // for models uploaded to EMEA change this option to 'derivativeV2_EU'

                        getAccessToken: function(onTokenReady) {
                            onTokenReady(tokenInfo.accessToken, tokenInfo.expiresIn);
                        }
                    };

                    Autodesk.Viewing.Initializer(options, async function () {
                        const htmlDiv = document.getElementById('autodeskViewer');
                        autodeskViewerRef.current = new Autodesk.Viewing.GuiViewer3D(htmlDiv);
                        // autodeskViewerRef.current.debugEvents(true);

                        const startedCode = autodeskViewerRef.current.start();
                        if (startedCode > 0) {
                            autodeskViewerRef.current.finish();
                            autodeskViewerRef.current = null;

                            reject('Failed to create a Viewer: WebGL not supported.');
                            return;
                        }

                        await autodeskViewerRef.current.loadExtension('Autodesk.Viewing.SceneBuilder');

                        //if (!__DEVELOPMENT__)
                        {
                            // autodeskViewerRef.current.setNavigationLockSettings({
                            //     orbit:false,
                            //     pan:false,
                            //     zoom:false,
                            //     roll:false,
                            //     fov:true,
                            //     walk:false,
                            //     gotoview:false
                            // });
                            //autodeskViewerRef.current.setNavigationLock(isAutodeskViewerLocked);
                        }

                        autodeskViewerRef.current.addEventListener(
                            Autodesk.Viewing.CAMERA_CHANGE_EVENT,
                            onAutodeskCameraChanged);
                        // autodeskViewerRef.current.addEventListener(
                        //     Autodesk.Viewing.VIEWER_RESIZE_EVENT,
                        //     onAutodeskViewerResized);
                        //
                        // autodeskViewerRef.current.setSelectionColor(
                        //     new THREE.Color(1,0,0),
                        //     Autodesk.Viewing.SelectionType.MIXED
                        // );
                        //
                        //
                        // // autodeskViewerRef.current.addEventListener(
                        // //     Autodesk.Viewing.EXTENSION_ACTIVATED_EVENT,
                        // //     evt => console.log('EXTENSION_ACTIVATED_EVENT', evt));
                        //
                        //
                        autodeskViewerRef.current.addEventListener(
                            Autodesk.Viewing.EXTENSION_LOADED_EVENT,
                            evt => {
                                // console.log('EXTENSION_LOADED_EVENT', evt)
                                if (evt.extensionId === 'Autodesk.BimWalk') {
                                    autodeskViewerRef.current.setDefaultNavigationTool('bimwalk');
                                    handleLockAutodeskNavigation(splitView);
                                }
                            });

                        resolve();
                    });
                })
                .catch((error) => {
                    console.error(error)
                    reject(error);
                })
            ;
        });
    }, []);


    const handleLockAutodeskNavigation = (lock, viewpointId) => {
        if (!autodeskViewerRef.current)
            return;

        autodeskViewerRef.current.setNavigationLock(lock);
        if (lock) {
            autodeskViewerRef.current.setNavigationLockSettings({
                orbit:false,
                pan:false,
                zoom:false,
                roll:true,
                fov:true,
                walk:false,
                gotoview:false
            });

            // window.frustum?.remove(2);
            // window.frustum?.draw();
            // setAutocadViewerCameraToViewpoint(viewpointId);
        }
        else {
            autodeskViewerRef.current.setNavigationLockSettings({
                // orbit:true,
                // pan:true,
                // zoom:true,
                // roll:true,
                // fov:true,
                // walk:true,
                // gotoview:true,
                orbit:false,
                pan:false,
                zoom:false,
                roll:false,
                fov:false,
                walk:false,
                gotoview:false
            });
            // handleAutodeskCameraChange(autodeskViewerRef.current.getCamera());
        }
        autodeskViewerRef.current.setDefaultNavigationTool('bimwalk');
    }


    const loadAutodeskViewable = useCallback((id) => {

        const onGeometryLoaded = async evt => {

            if (evt.model.isSceneBuilder()) {
                //if (evt.model.getModelId() !== 1) {
                return;
            }

            autodeskViewerRef.current.removeEventListener(
                Autodesk.Viewing.MODEL_ROOT_LOADED_EVENT,
                onGeometryLoaded
            );

            modelViewData.current.refPointTransformation = [
                1,0,0,
                0,1,0,
                0,0,1,
                0,0,0
            ];

            try {
                const docNode = evt.model.getDocumentNode();
                const doc = docNode.getDocument();

                await doc.downloadAecModelData();

                const aec = docNode.getAecModelData();

                modelViewData.current.refPointTransformation = aec.refPointTransformation;

                // console.log('model', evt.model)
                // console.log('docNode', docNode)
                // console.log('doc', doc)
                // console.log('aecModelData', aec)
            }
            catch {
            }

            const { paperToModel, modelToPaper } = createModelPlanMatrices(selectedPlan);

            modelViewData.current.paperToModel = paperToModel;
            modelViewData.current.modelToPaper = modelToPaper;

            const selectedViewPoint = selectedPlan.viewpoints.find(vp => vp.id === selectedViewpointId);
            setAutocadViewerCameraToViewpoint(selectedPlan, selectedViewPoint);
        };

        return new Promise((resolve, reject) => {

            if (!autodeskViewerRef.current) {
                reject(autodeskViewerRef.current);
                return;
            }

            const perspectiveCamera = () => {
                autodeskViewerRef.current.autocam.homeVector.isPerspective = true;
                autodeskViewerRef.current.autocam.homeVector.isOrtho = false;
                autodeskViewerRef.current.autocam.originalHomeVector.isPerspective = true;
                autodeskViewerRef.current.autocam.originalHomeVector.isOrtho = false;
                autodeskViewerRef.current.autocam.toPerspective();

                autodeskViewerRef.current.removeEventListener(
                    Autodesk.Viewing.MODEL_ROOT_LOADED_EVENT,
                    perspectiveCamera
                );
            }

            autodeskViewerRef.current.addEventListener(
                Autodesk.Viewing.MODEL_ROOT_LOADED_EVENT,
                perspectiveCamera
            );

            const models = autodeskViewerRef.current.impl.modelQueue().getModels();
            for (const model of models) {
                autodeskViewerRef.current.impl.unloadModel(model);
            }

            async function onDocumentLoadSuccess(viewerDocument) {
                const rootNode = viewerDocument.getRoot();
                const defaultModel = viewerDocument.getRoot().getDefaultGeometry();

                autodeskViewerRef.current.loadDocumentNode(viewerDocument, defaultModel, {
                    applyRefPoint:true,
                });

                resolve();
            }


            function onDocumentLoadFailure() {
                console.error('Failed fetching Forge manifest');
                reject('Failed fetching Forge manifest');
            }

            autodeskViewerRef.current.addEventListener(
                Autodesk.Viewing.MODEL_ROOT_LOADED_EVENT,
                onGeometryLoaded
            );

            Autodesk.Viewing.Document.load(
                `urn:${id}`,
                onDocumentLoadSuccess,
                onDocumentLoadFailure);
        });
    }, []);


    const handleAutodeskCameraChange = useCallback(camera => {

        let camPos = camera.position.clone();
        let camTrg = camera.target.clone();

        if (autodeskViewerRef.current.model) {
            try {
                camPos = camPos.applyMatrix4(dashboardDataRef.current.refPointTr);
                camTrg = camTrg.applyMatrix4(dashboardDataRef.current.refPointTr);

                camPos = camPos.applyMatrix4(autodeskViewerRef.current.model.getInverseModelToViewerTransform());
                camTrg = camTrg.applyMatrix4(autodeskViewerRef.current.model.getInverseModelToViewerTransform());

                camPos = camPos.applyMatrix4(dashboardDataRef.current.modelToPaper);
                camTrg = camTrg.applyMatrix4(dashboardDataRef.current.modelToPaper);
            }
            catch {}
        }

        let avec = (new THREE.Vector3()).subVectors(camTrg, camPos).setZ(0);
        let xvec = new THREE.Vector3().setX(1);
        let angle = avec.angleTo(xvec);

        let xc = xvec. x * avec.y - xvec.y * avec.x;
        if (xc < 0) angle = Math.PI * 2 - angle;

        angle = 2 * Math.PI - (angle - Math.PI/2);
        if (angle > 2 * Math.PI) angle -= 2 * Math.PI;


        let pvec = (new THREE.Vector3()).subVectors(camTrg, camPos).normalize();
        let pxvec = new THREE.Vector3(pvec.x, pvec.y, 0).normalize();

        let pcross = pxvec.clone().cross(pvec);
        let pcrossN = pcross.clone().normalize();
        let pangle = (pvec.z >= 0 ? 1 : -1) * Math.atan2(pcross.dot(pcrossN), pxvec.dot(pvec));

        angle *= 180.0 / Math.PI;
        pangle *= 180.0 / Math.PI;
        if (pangle <= -70)
            pangle = -70;
        else if (pangle > 70)
            pangle = 70;

        setYaw && setYaw(angle);
        setPitch && setPitch(pangle);
    }, []);


    const onAutodeskCameraChanged = useCallback(evt => {

        if (splitView)
            return;

        handleAutodeskCameraChange(evt.camera);
    });


    useEffect(() => {
        initializeAutodeskViewer()
            .then(() => {
                const { paperToModel, modelToPaper } = createModelPlanMatrices(selectedPlan);

                modelViewData.current.paperToModel = paperToModel;
                modelViewData.current.modelToPaper = modelToPaper;
            })
            .catch(err => console.error('** ' + err));

        return () => {
            autodeskViewerRef.current?.finish();
            autodeskViewerRef.current = null;
        };
    }, []);


    useEffect(() => {

        a360GetProjectItem(a360_project, a360_file)
            .then(response => {
                const item = response.data.data;
                const versions = response.data.included;

                const tip = xHas(item, 'relationships.tip.data') ? item.relationships.tip.data : null;
                const version = versions.find(v => v.id === tip.id);

                const derivative = version.relationships.derivatives.data.id;

                loadAutodeskViewable(derivative)
                    .then(() => {
                        // dashboardDataRef.current.a360model.hub = projectPlan.a360_hub;
                        // dashboardDataRef.current.a360model.project = projectPlan.a360_project;
                        // dashboardDataRef.current.a360model.file = projectPlan.a360_file;
                        // dashboardDataRef.current.a360model.derivative = derivative;
                    })
                    .catch(err => console.error(err))
                ;
            })
            .catch(err => console.error(err))
        ;
    }, [a360_hub, a360_project, a360_file]);


    useEffect(() => {
        updateAutocadViewerCameraTarget(360 - (leftYaw - 90), leftPitch, leftHfov);
    }, [leftYaw, leftPitch, leftHfov]);


    useEffect(() => {
        const { paperToModel, modelToPaper } = createModelPlanMatrices(selectedPlan);

        modelViewData.current.paperToModel = paperToModel;
        modelViewData.current.modelToPaper = modelToPaper;

        const selectedViewPoint = selectedPlan.viewpoints.find(vp => vp.id === selectedViewpointId);
        setAutocadViewerCameraToViewpoint(selectedPlan, selectedViewPoint);
    }, [selectedPlan, selectedViewpointId]);


    return (
        <>
            <div ref={autodeskViewerContainerRef}
                 id={"autodeskViewerContainer"}
                 style={{
                position: 'relative',
                width:'100%',
                height:'100%',
                padding:2,
                border: '4px solid orange',
                visibility: visible ? 'visible' : 'hidden',
            }}>
                <div id="autodeskViewer" style={{
                    position: 'absolute',
                    left:0,
                    top:0,
                    right:0,
                    bottom:0,
                }}></div>
            </div>
        </>
    );
}


export default React.forwardRef(AutodeskModelView);


AutodeskModelView.propTypes = {
    visible:PropTypes.bool.isRequired,
    a360_hub:PropTypes.string.isRequired,
    a360_project:PropTypes.string.isRequired,
    a360_file:PropTypes.string.isRequired,
    setYaw:PropTypes.func,
    setPitch:PropTypes.func,
}